import { toast } from '@crabs-ai/ds-react';
import { EditorStarterAsset } from '../assets/assets';
import { SetState } from '../context-provider';
import { finishUpload } from '../state/actions/finish-upload';
import { setUploadError } from '../state/actions/set-upload-error';
import { setUploadProgress } from '../state/actions/set-upload-progress';
import { PresignResult, uploadWithProgressUpdates } from './use-uploader';

export interface UploadResult {
  presignedUrl: string;
  readUrl: string;
  fileKey: string;
}

/**
 * unknown error에서 안전하게 error message를 얻는 utility function
 */
export const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  return 'Unknown error occurred';
};

/**
 * unknown error에서 안전하게 error stack을 얻는 utility function
 */
export const getErrorStack = (error: unknown): string => {
  if (error instanceof Error) {
    return error.stack || error.message;
  }
  return 'Unknown error';
};

/**
 * progress tracking과 error handling을 통해 upload 시도를 처리
 */
export const attemptAssetUpload = async ({
  uploadUrls,
  setState,
  asset,
  file,
}: {
  uploadUrls: UploadResult;
  setState: SetState;
  asset: EditorStarterAsset;
  file: Blob;
}): Promise<UploadResult> => {
  await uploadWithProgressUpdates(
    file,
    uploadUrls.presignedUrl,
    (uploadProgress) => {
      setState({
        update: (state) => {
          return setUploadProgress({ state, asset, uploadProgress });
        },
        commitToUndoStack: false,
      });
    },
  );

  setState({
    update: (state) => {
      return finishUpload({
        state,
        asset,
        remoteUrl: uploadUrls.readUrl,
        remoteFileKey: uploadUrls.fileKey,
      });
    },
    commitToUndoStack: false,
  });

  return uploadUrls;
};

/**
 * 완전한 upload workflow - URL 검색, upload 시도, error 상태를 처리
 */
export const performAssetUpload = async ({
  setState,
  asset,
  presignResultPromise,
  file,
}: {
  setState: SetState;
  asset: EditorStarterAsset;
  presignResultPromise: Promise<PresignResult>;
  file: Blob;
}): Promise<void> => {
  const presignResult = await presignResultPromise;
  if (presignResult.type === 'file-too-large') {
    setState({
      update: (state) => {
        return setUploadError({
          state,
          asset: asset,
          error: new Error('File too large to upload'),
          canRetry: false,
        });
      },
      commitToUndoStack: false,
    });
    toast({ type: 'error', description: 'The file is too large to upload' });

    return;
  }

  if (presignResult.type === 'no-credentials') {
    setState({
      update: (state) => {
        return setUploadError({
          state,
          asset: asset,
          error: new Error(
            'AWS credentials not configured - https://www.remotion.dev/docs/editor-starter/asset-uploads',
          ),
          canRetry: true,
        });
      },
      commitToUndoStack: false,
    });
    toast({ type: 'error', description: 'Upload failed: No credentials' });
    console.error(
      'AWS Asset upload failed. Did you set up AWS credentials? https://www.remotion.dev/docs/editor-starter/asset-uploads',
    );
    return;
  }

  if (presignResult.type === 'error') {
    setState({
      update: (state) => {
        return setUploadError({
          state,
          asset: asset,
          error: new Error(presignResult.error as string),
          canRetry: true,
        });
      },
      commitToUndoStack: false,
    });
    console.error(presignResult.error);
    toast({ type: 'error', description: 'Upload failed' });
    return;
  }

  try {
    // upload 시도
    await attemptAssetUpload({
      uploadUrls: presignResult.result,
      setState,
      asset,
      file,
    });
  } catch (uploadError) {
    setState({
      update: (state) => {
        return setUploadError({
          state,
          asset: asset,
          error: uploadError as Error,
          canRetry: true,
        });
      },
      commitToUndoStack: false,
    });
    toast({ type: 'error', description: 'Upload failed' });
    console.error(uploadError);
  }
};
